<script setup lang="ts">
import { computed } from 'vue';

export type BadgeSize = 's' | 'm' | 'l';
export type BadgeColor = 'gray' | 'blue' | 'red' | 'yellow';

const {
  value,
  color = 'gray',
  size = 'm',
} = defineProps<{
  value: string | number;
  size?: BadgeSize;
  color?: BadgeColor;
  pulse?: boolean;
}>();

const wide = computed(() => value.toString().length > 2);
</script>

<template>
  <span
    class="ui-badge"
    :class="[`_size-${size}`, `_color-${color}`, { _wide: wide, _pulse: pulse }]"
  >
    {{ value }}
  </span>
</template>

<style scoped lang="scss">
.ui-badge {
  position: relative;
  z-index: 0;

  display: inline-block;

  text-align: center;
  color: var(--color-white);
  font-weight: var(--font-weight-bold);
  border-radius: 100px;

  transition: background-color var(--transition);
  will-change: transform;

  @mixin size($breakpoint, $size, $padding, $font-size) {
    &._size-#{$breakpoint} {
      height: $size;
      line-height: $size;

      font-size: $font-size;

      &:not(._wide) {
        width: $size;
      }

      &._wide {
        padding: 0 $padding;
      }
    }
  }

  @include size(s, 16px, 6px, var(--font-size-xs));
  @include size(m, 18px, 6px, var(--font-size-s));
  @include size(l, 24px, 6px, var(--font-size));

  &._pulse {
    @keyframes pulse-scale {
      0%,
      100% {
        transform: scale(0.9);
      }
      70% {
        transform: scale(1);
      }
    }

    @keyframes pulse-ring {
      0% {
        transform: scale(1);
        opacity: 0.8;
      }
      70%,
      100% {
        transform: scale(1.6);
        opacity: 0;
      }
    }

    animation: pulse-scale 2s infinite;

    &::after {
      content: '';

      position: absolute;
      z-index: -1;
      inset: 0;

      border-radius: inherit;
      background-color: inherit;

      will-change: transform, opacity;
      animation: pulse-ring 2s infinite;
    }
  }

  @mixin color($name, $color) {
    &._color-#{$name} {
      background-color: $color;
    }
  }

  @include color('gray', var(--color-gray-400));
  @include color('blue', var(--color-blue-600));
  @include color('red', var(--color-red-1000));
  @include color('yellow', var(--color-yellow-400));

  &._color-yellow {
    color: var(--color-gray-1000);
    font-weight: var(--font-weight);
  }
}
</style>
